/**
 * src/api/releaseFlagsApi.js
 *
 * Thin client for release flags (RPDA-04, RPDA-05). The read resolves every
 * flag server-side for the caller's own tenant (release-flags-read); nothing
 * here sends a tenant id for a tenant session (resolveTenantId, AUTH-B-002).
 * Writes go through release-flag-write only. The super-admin Releases tab
 * routes them to one environment's Function App with { env } (STG-B-002);
 * on production the shared transport re-verifies MFA (StepUpDialog, §16).
 */

import { azureGet, azureEnvGet, azureEnvPost } from './azureClient';

/** { environment, flags: [{ key, project, description, on, source }] } */
export async function getReleaseFlags(params = {}) {
  return azureGet('/api/release-flags-read', params);
}

/** Super-admin: one environment's registry with raw records; `tenantId` adds that tenant's override. */
export async function readReleaseFlagsAdmin({ env, tenantId } = {}) {
  return azureEnvGet(env, '/api/release-flags-read', tenantId ? { tenantId } : {});
}

/**
 * Super-admin: every tenant of the environment for one flag, resolved
 * server-side (release-flags-read?scope=tenants&key=) — one call.
 */
export async function readReleaseFlagTenants({ env, key }) {
  return azureEnvGet(env, '/api/release-flags-read', { scope: 'tenants', key });
}

/**
 * Super-admin: the named holders report for one flag (release cutover) —
 * { environment, key, cutoverOpenUntil, holders: [{ tenantId, tenantName,
 * recordCount }], unknown: [{ tenantId, tenantName }] }.
 */
export async function readReleaseFlagHolders({ env, key }) {
  return azureEnvGet(env, '/api/release-flags-read', { scope: 'holders', key });
}

/** Super-admin: the one write path. Body: { scope, tenantId?, key, value, expected, cutover? }. */
export async function writeReleaseFlag({ env, body }) {
  return azureEnvPost(env, '/api/release-flag-write', body);
}

/**
 * Super-admin: every open re-enable request of a halted workflow across the
 * environment's tenants (PDW-HALT-2 slice 4b, workflow-halt-resume-requests) —
 * { count, truncated, requests: [{ tenantId, reviewId, definitionId,
 * definitionName, requestedBy, requestedByName, requestedAt, ageDays, reason,
 * decidedAt, rowVersion }] }. `count` is exact, never the list length.
 */
export async function readHaltResumeRequests({ env = 'production' } = {}) {
  return azureEnvGet(env, '/api/workflow-halt-resume-requests', {});
}

/**
 * Super-admin: decline one open re-enable request with a reason (10-500). The
 * only explicit answer — switching back on is the release flag itself.
 * @throws ApiError 409 { field: 'resumeRequest' | 'decision', yours, landed } or reason 'flag-on'
 */
export async function declineHaltResumeRequest({
  env = 'production',
  tenantId,
  reviewId,
  rowVersion,
  decidedAt,
  reason,
}) {
  // `decidedAt` is the listed row's: a tenant re-decision since is named once.
  return azureEnvPost(env, '/api/workflow-halt-resume-requests', {
    tenantId,
    reviewId,
    rowVersion,
    answer: 'decline',
    reason,
    decidedAt,
  });
}
